/**
 * BusinessProfileStep — paso 1 del setup wizard.
 *
 * Configura el perfil de negocio del número: nombre comercial, categoría,
 * descripción, horario, web, dirección, foto. Equivalente a lo que Meta
 * llama "WhatsApp Business Profile".
 */

import { useState } from "react";
import { Loader2, Save, Upload, Building2 } from "lucide-react";
import { Button } from "../../../../shared/components/ui/Button";
import { whatsappAccountsApi } from "../../api";
import type {
    WhatsAppAccount,
    WhatsAppBusinessProfile,
} from "../../types";

interface Props {
    account: WhatsAppAccount;
    onComplete: (updated: WhatsAppAccount) => void;
}

const CATEGORIES: { value: WhatsAppBusinessProfile["category"]; label: string }[] = [
    { value: "restaurant", label: "Restaurante / Gastronomía" },
    { value: "retail", label: "Tienda / Retail" },
    { value: "service", label: "Servicios profesionales" },
    { value: "ecommerce", label: "E-commerce" },
    { value: "other", label: "Otro" },
];

export default function BusinessProfileStep({ account, onComplete }: Props) {
    const existing = account.businessProfile;
    const [profile, setProfile] = useState<WhatsAppBusinessProfile>({
        displayName: existing?.displayName || account.displayName,
        category: existing?.category || "restaurant",
        description: existing?.description || "",
        hours: existing?.hours || "",
        website: existing?.website || "",
        address: existing?.address || "",
        profilePhoto: existing?.profilePhoto || "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSave = async () => {
        if (!profile.displayName.trim()) {
            setError("El nombre comercial es requerido");
            return;
        }
        setSaving(true);
        setError(null);
        const res = await whatsappAccountsApi.saveBusinessProfile(account.id, profile);
        setSaving(false);
        if (!res.success) {
            setError(res.error || "Error al guardar");
            return;
        }
        onComplete(res.data);
    };

    return (
        <div className="space-y-5">
            <SectionHeader
                icon={<Building2 size={16} />}
                title="Perfil de negocio"
                subtitle="Lo que ven tus clientes cuando reciben el primer mensaje."
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Logo placeholder */}
                <div className="md:col-span-2 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-200 text-green-700 flex items-center justify-center text-2xl font-black shadow-sm flex-shrink-0">
                        {profile.displayName.slice(0, 2).toUpperCase() || "LB"}
                    </div>
                    <button
                        type="button"
                        disabled
                        title="Disponible próximamente"
                        className="text-xs font-medium text-slate-400 flex items-center gap-1.5 cursor-not-allowed"
                    >
                        <Upload size={12} />
                        Subir foto (pronto)
                    </button>
                </div>

                <Field
                    label="Nombre comercial *"
                    value={profile.displayName}
                    onChange={(v) => setProfile((p) => ({ ...p, displayName: v }))}
                    placeholder="La Bistecca"
                />

                <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                        Categoría
                    </label>
                    <select
                        value={profile.category}
                        onChange={(e) =>
                            setProfile((p) => ({
                                ...p,
                                category: e.target.value as any,
                            }))
                        }
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-green-300 cursor-pointer"
                    >
                        {CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>
                                {c.label}
                            </option>
                        ))}
                    </select>
                </div>

                <Field
                    label="Descripción"
                    value={profile.description || ""}
                    onChange={(v) => setProfile((p) => ({ ...p, description: v }))}
                    placeholder="Pizza, pastas y delivery en CABA"
                    className="md:col-span-2"
                />

                <Field
                    label="Horario"
                    value={profile.hours || ""}
                    onChange={(v) => setProfile((p) => ({ ...p, hours: v }))}
                    placeholder="L-V 11-23, S-D 12-24"
                />
                <Field
                    label="Sitio web"
                    value={profile.website || ""}
                    onChange={(v) => setProfile((p) => ({ ...p, website: v }))}
                    placeholder="https://tumarca.com"
                />
                <Field
                    label="Dirección"
                    value={profile.address || ""}
                    onChange={(v) => setProfile((p) => ({ ...p, address: v }))}
                    placeholder="Av. Santa Fe 4200, CABA"
                    className="md:col-span-2"
                />
            </div>

            {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">
                    {error}
                </div>
            )}

            <div className="flex justify-end">
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || !profile.displayName.trim()}
                >
                    {saving ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <Save size={14} />
                    )}
                    Guardar y continuar
                </Button>
            </div>
        </div>
    );
}

function SectionHeader({
    icon,
    title,
    subtitle,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
}) {
    return (
        <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-violet-50 text-violet-600">{icon}</div>
            <div>
                <h3 className="text-base font-bold text-slate-900">{title}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
            </div>
        </div>
    );
}

function Field({
    label,
    value,
    onChange,
    placeholder,
    className,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    className?: string;
}) {
    return (
        <div className={className}>
            <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                {label}
            </label>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-green-300 focus:ring-1 focus:ring-green-100"
            />
        </div>
    );
}
